import { ChangeEvent, FC, useEffect, useState } from 'react';
import { Paper, Grid, Button, Checkbox, FormControl, Select, MenuItem } from '@material-ui/core';
import { Tab, Tabs } from '@material-ui/core';
import { useStyles } from './styles';
import { observer } from 'mobx-react-lite';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import { Controller, useForm } from 'react-hook-form';
import { useStore } from 'hooks/useRootStore';
import { useMutation, useQuery } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { ZoomProfile } from './types';
import TabPanel from 'components/TabPanel';
import { useLocation } from 'react-router-dom';
import { useStylesSecond } from './styles';
import WebAssetIcon from '@material-ui/icons/WebAsset';
import { getFieldConfig } from '../../../utils/fieldConfigUtils';

// Add type definitions for field configuration
// interface FieldConfig {
//   destKey?: string;
//   destLabel?: string;
//   sourseKey?: string;
//   sourceKey?: string;
//   sourseLabel?: string;
//   sourceLabel?: string;
//   isFromPreference?: boolean;
//   defaultValue?: string;
// }
function useQuerys() {
  return new URLSearchParams(useLocation().search);
}

export const ZoomInfoTable: FC = observer(() => {
  const classes = {
    ...useStyles(),
    ...useStylesSecond(),
  };
  const [zoomProfile, setZoomProfile] = useState<ZoomProfile | undefined>(undefined);
  const [infoAttributeMappings, setInfoAttributeMappings] = useState<{ [key: string]: { [key: string]: string } }>({
    people: {},
    group: {},
    department: {},
  });
  const [mappingsInitialized, setMappingsInitialized] = useState(false);
  const {
    AppStore: { appConnect, ZoomKeyGenrates, getAttributePreferences, AttributePreferences },
    notificationStore: { openNotification },
  } = useStore();

  useEffect(() => {
    document.title = 'EasyIdentity: App: InfoDetail';
  }, []);

  const zoomConnectedReq = useQuery('zoomConnectedReq', () => appConnect(), {
    onError: () => openNotification('error', `Can't connect with zoom`),
  });

  const { control, handleSubmit, setValue } = useForm<any>({
    defaultValues: {
      is_sync: false,
    },
  });
  const getAttributePreferencesReq = useQuery('getAttributePreferencesReq', () => getAttributePreferences('zoom'), {
    onError: () => openNotification('error', `Can't get attribute`),
  });

  const infoData = getAttributePreferencesReq?.data?.data;
  const getAttributesByEntity = (entity: string) => {
    if (!infoData?.attributes) {
      return [];
    }
    const filtered = infoData.attributes.filter((attr: any) => attr.entity === entity);
    return filtered;
  };

  const getZoomAttributesByEntity = (entity: string) => {
    if (!infoData?.attributes) return [];

    const uniqueOptions = new Map();

    // Add static None option first
    uniqueOptions.set('', { sourseKey: '', sourseLabel: 'None' });

    // Collect unique source options - preference takes priority over default
    infoData.attributes
      .filter((attr: any) => attr.entity === entity)
      .forEach((attr: any) => {
        // Check if preference exists and has valid data
        const hasPreference =
          attr.preference &&
          (attr.preference.sourseKey || attr.preference.sourceKey) &&
          (attr.preference.sourseKey !== '' || attr.preference.sourceKey !== '');

        if (hasPreference) {
          // Use preference only if it exists
          const key = attr.preference.sourseKey || attr.preference.sourceKey;
          const label = attr.preference.sourseLabel || attr.preference.sourceLabel || key;

          if (!uniqueOptions.has(key)) {
            uniqueOptions.set(key, { sourseKey: key, sourseLabel: label });
          }
        } else if (attr.default?.sourseKey || attr.default?.sourceKey) {
          // Use default only if no preference exists
          const key = attr.default.sourseKey || attr.default.sourceKey;
          const label = attr.default.sourseLabel || attr.default.sourceLabel || key;

          if (!uniqueOptions.has(key)) {
            uniqueOptions.set(key, { sourseKey: key, sourseLabel: label });
          }
        }
      });

    const options = Array.from(uniqueOptions.values());
    return options;
  };

  const isAttributeEditable = (destKey: string) => {
    return true;
  };

  const isZoomAttributeFixed = (sourseKey: string) => {
    const fixedZoomFields = [
      'primaryEmail', // Email - fixed
      'password',
      'id',
      'email',
      // Password - fixed
    ];
    return fixedZoomFields.includes(sourseKey);
  };

  useEffect(() => {
    if (zoomConnectedReq?.data) {
      zoomConnectedReq.data.forEach((item: any) => {
        if (item.provider === 'zoom') {
          setZoomProfile(item);
        }
      });
    }
  }, [zoomConnectedReq?.data]);

  useEffect(() => {
    if (zoomProfile) {
      setValue('is_sync', zoomProfile.is_sync);
    }
  }, [zoomProfile, setValue]);

  const ZoomKeyGenratesreq = useMutation((data: any) => ZoomKeyGenrates(data), {
    mutationKey: 'ZoomKeyGenratesreq',
    onSuccess: (data) => {
      openNotification('success', 'Saved!!');
      zoomConnectedReq.refetch();
      localStorage.removeItem('open');
    },
    onError: (error) => {
      openNotification('error', `Failed to save`);
    },
  });

  const AttributePreferencesReq = useMutation((data: any) => AttributePreferences(data), {
    mutationKey: 'AttributePreferencesReq',
    onSuccess: (data) => {
      openNotification('success', 'Attribute mappings saved!');
      getAttributePreferencesReq.refetch(); // Refresh the data
      setInfoAttributeMappings({
        people: {},
        group: {},
        department: {},
      });
    },
    onError: (error) => {
      openNotification('error', 'Failed to save attribute mappings');
    },
  });

  const onSubmit = (data: any) => {
    ZoomKeyGenratesreq.mutate(data);
  };

  function a11yProps(index: string) {
    return {
      id: `wrapped-tab-${index}`,
      'aria-controls': `wrapped-tabpanel-${index}`,
    };
  }
  const query = useQuerys();
  const queryValue = query.get('value');
  const defaultTabs = (Array.isArray(queryValue) ? queryValue[0] : queryValue) ?? 'one';

  const [value, setValues] = useState<string>(defaultTabs);

  const handleChange = (event: ChangeEvent<unknown>, newValue: string) => {
    setValues(newValue);
  };

  const getCurrentInfoTabMappings = () => {
    const currentEntity = value === 'one' ? 'people' : value === 'two' ? 'group' : 'department';
    const mappings = infoAttributeMappings[currentEntity] || {};
    return mappings;
  };

  const handleInfoAttributeMapping = (destKey: string, sourseKey: string) => {
    const tabKey = value === 'one' ? 'people' : value === 'two' ? 'group' : 'department';
    setInfoAttributeMappings((prev) => {
      const newMappings = { ...prev };

      // Clear the selected value from ALL other fields first to prevent duplicates
      if (sourseKey && sourseKey !== '') {
        Object.keys(newMappings[tabKey]).forEach((key) => {
          if (key !== destKey && newMappings[tabKey][key] === sourseKey) {
            newMappings[tabKey][key] = '';
          }
        });
      }

      // Set the new mapping (empty string for None selection or no selection)
      newMappings[tabKey] = {
        ...newMappings[tabKey],
        [destKey]: sourseKey || '', // Ensure empty string, never null
      };
      return newMappings;
    });
  };

  const saveInfoAttributeMappings = () => {
    const allEntities = ['people', 'group', 'department'];
    const allRequiredFields: any[] = [];
    allEntities.forEach((entity) => {
      const entityAttributes = getAttributesByEntity(entity);
      const requiredFields = entityAttributes
        .filter((attr: any) => attr.required)
        .filter((attr: any) => {
          const fieldConfig = getFieldConfig(attr);
          if (!fieldConfig.destKey) return false;
          const tabKey = entity;
          return !infoAttributeMappings[tabKey]?.[fieldConfig.destKey];
        });

      allRequiredFields.push(...requiredFields.map((field: any) => ({ ...field, entity })));
    });

    if (allRequiredFields.length > 0) {
      const fieldNames = allRequiredFields
        .map((field: any) => {
          const fieldConfig = getFieldConfig(field);
          return `${fieldConfig.destLabel || 'Unknown'} (${field.entity})`;
        })
        .join(', ');
      openNotification('error', `Please map all required fields: ${fieldNames}`);
      return;
    }

    const payload = {
      app_name: 'zoom',
      attributes: [] as any[],
    };

    const allMappings = {
      people: infoAttributeMappings.people || {},
      group: infoAttributeMappings.group || {},
      department: infoAttributeMappings.department || {},
    };

    Object.entries(allMappings).forEach(([entity, mappings]) => {
      const allEntityAttributes = getAttributesByEntity(entity);
      allEntityAttributes.forEach((originalAttr: any) => {
        const fieldConfig = getFieldConfig(originalAttr);
        let destKey = fieldConfig.destKey;

        // Handle special case where destKey might be stored in a different property
        if (!destKey && originalAttr.default) {
          // Check if any property in default looks like a destKey
          Object.keys(originalAttr.default).forEach((key) => {
            if (
              key !== 'destKey' &&
              key !== 'destLabel' &&
              key !== 'sourseKey' &&
              key !== 'sourseLabel' &&
              key !== 'defaultValue'
            ) {
              // This might be the destKey stored in a different property
              destKey = key;
            }
          });
        }

        if (destKey) {
          const sourseKey = mappings[destKey] || '';
          const selectedZoomAttr = sourseKey
            ? getZoomAttributesByEntity(entity).find((attr: any) => attr.sourseKey === sourseKey)
            : null;
          // Always use the mapping values, not the original attribute values
          const cleanSourceKey = sourseKey || ''; // Use mapping value
          const cleanSourceLabel = selectedZoomAttr?.sourseLabel || sourseKey || ''; // Use selected option label
          const attributeItem = {
            type: originalAttr.type,
            entity,
            hidden: originalAttr.hidden,
            required: originalAttr.required,
            default: originalAttr.default, // Include original default
            preference: {
              destKey,
              destLabel: fieldConfig.destLabel || '',
              sourseKey: cleanSourceKey, // Use mapping value, not original preference
              sourseLabel: cleanSourceLabel, // Use selected option label, not original preference
            },
          };
          payload.attributes.push(attributeItem);
        } else {
          console.log('Skipping attribute (no destKey):', originalAttr);
        }
      });
    });
    AttributePreferencesReq.mutate(payload);
  };

  // Initialize mappings from API data
  useEffect(() => {
    if (infoData?.attributes) {
      setMappingsInitialized(false); // Reset flag

      const mappings = {
        people: {} as { [key: string]: string },
        group: {} as { [key: string]: string },
        department: {} as { [key: string]: string },
      };

      infoData.attributes.forEach((attr: any) => {
        const fieldConfig = getFieldConfig(attr);
        if (fieldConfig.destKey) {
          let selectedValue = '';

          // Check if preference exists and has a non-empty sourseKey/sourceKey
          const preferenceKey = attr.preference?.sourseKey || attr.preference?.sourceKey;
          // const defaultKey = attr.default?.sourseKey || attr.default?.sourceKey;
          // If preference is null or empty, show None (empty string)
          // Only use preference if it has a valid non-null value
          if (
            preferenceKey &&
            preferenceKey !== null &&
            preferenceKey !== 'null' &&
            preferenceKey.toString().trim() !== ''
          ) {
            // Use preference value if it's valid
            selectedValue = preferenceKey;
          } else {
            // Show None when preference is null/empty (don't use default)
            selectedValue = '';
          }
          mappings[attr.entity as keyof typeof mappings][fieldConfig.destKey] = selectedValue;
        }
      });

      setInfoAttributeMappings(mappings);

      // Set flag after a small delay to ensure state is updated
      setTimeout(() => {
        setMappingsInitialized(true);
      }, 100);
    }
  }, [infoData]);

  const isZoomAttributeDisabled = (googleKey: string, currentLdapKey: string) => {
    // Allow all selections - no restrictions on duplicates
    return false;
  };

  const handleNext = () => {
    const currentEntity = value === 'one' ? 'people' : 'group';
    const requiredFields = getAttributesByEntity(currentEntity)
      .filter((attr: any) => attr.required)
      .filter((attr: any) => {
        const fieldConfig = getFieldConfig(attr);
        if (!fieldConfig.destKey) return false;
        return !getCurrentInfoTabMappings()[fieldConfig.destKey];
      });

    if (requiredFields.length > 0) {
      const fieldNames = requiredFields
        .map((field: any) => {
          const fieldConfig = getFieldConfig(field);
          return fieldConfig.destLabel || 'Unknown';
        })
        .join(', ');
      openNotification('error', `Please map required fields: ${fieldNames}`);
      return;
    }

    if (value === 'one') setValues('two');
    else if (value === 'two') setValues('three');
  };

  const handleBack = () => {
    if (value === 'three') setValues('two');
    else if (value === 'two') setValues('one');
  };

  const isLastTab = value === 'two';
  const isFirstTab = value === 'one';

  useAppPreloader(ZoomKeyGenratesreq.isLoading || zoomConnectedReq.isFetching || AttributePreferencesReq.isLoading);
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper className={classes.paper}>
          <div className={classes.leftCol}>
            <Grid item xs={12} className={classes.btnContainer}>
              <Button className={classes.submitBtn} type='submit' variant='contained'>
                Save changes
              </Button>
            </Grid>
          </div>
          <TableContainer>
            <Table className={`${classes.border} ${classes.boxes}`} aria-label='simple table'>
              <TableBody>
                <TableRow>
                  <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                    Automatically Sync All Users, Groups and Departments with zoom Workspace (bi-directional)
                  </TableCell>
                  <TableCell className={classes.border}>
                    <Controller
                      key='is_sync'
                      name='is_sync'
                      control={control}
                      render={({ field }) => (
                        <Grid container className={classes.row}>
                          <Checkbox
                            className={classes.tableCheck}
                            checked={field.value === true}
                            onChange={(event: ChangeEvent<HTMLInputElement>, checked: boolean) =>
                              field.onChange(checked ? true : false)
                            }
                          />
                        </Grid>
                      )}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </form>
      <div className={classes.mappingHeader}>
        <h2 className={classes.mappingTitle}>Configure Attribute Mapping</h2>
        <p className={classes.mappingSubText}>Map LDAP attributes to their corresponding Zoom Workspace fields.</p>
      </div>
      <Grid item xl className={classes.tabsPanels}>
        <Paper className={classes.tabsPaper}>
          <Tabs
            value={value}
            className={classes.tabs}
            TabIndicatorProps={{ style: { color: 'transparent', background: 'transparent' } }}
            onChange={handleChange}
          >
            <Tab value='one' wrapped label={<span>Users</span>} {...a11yProps('one')} className={classes.label} />
            <Tab value='two' wrapped label={<span>Groups</span>} {...a11yProps('two')} className={classes.label} />
            {/* <Tab value='three' wrapped label={<span>Departments</span>} {...a11yProps('three')} className={classes.label} /> */}
          </Tabs>

          <div className={classes.tabBox}>
            <TabPanel value={value} index='one'>
              <TableContainer>
                <Table className={`${classes.border} ${classes.boxes}`} aria-label='user attribute mapping table'>
                  <TableHead>
                    <TableRow>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>LDAP Attributes</TableCell>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>Zoom Attributes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getAttributesByEntity('people')
                      .map((attr: any, index: number) => {
                        const fieldConfig = getFieldConfig(attr);
                        // Show row if it has either destKey or sourseKey (for display purposes)
                        if (!fieldConfig.destKey && !fieldConfig.sourseKey && !fieldConfig.sourceKey) return null;

                        return (
                          <TableRow key={index}>
                            <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                              {fieldConfig.destLabel || 'Unknown'}
                              {attr.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                              {fieldConfig.isFromPreference && <span style={{ color: 'blue', marginLeft: 4 }}></span>}
                            </TableCell>
                            <TableCell className={`${classes.border} ${classes.cellLink}`}>
                              {isZoomAttributeFixed(fieldConfig.sourseKey || fieldConfig.sourceKey || '') ? (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              ) : isAttributeEditable(fieldConfig.destKey || '') ? (
                                <FormControl fullWidth>
                                  <Select
                                    key={`${fieldConfig.destKey}-${
                                      getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || 'empty'
                                    }-${mappingsInitialized}`}
                                    value={(() => {
                                      const selectedValue =
                                        getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || '';
                                      // const availableOptions = getZoomAttributesByEntity('people');
                                      // const optionExists = availableOptions.some(opt => opt.sourseKey === selectedValue);
                                      return selectedValue;
                                    })()}
                                    onChange={(e) => {
                                      handleInfoAttributeMapping(fieldConfig.destKey || '', e.target.value as string);
                                    }}
                                    displayEmpty
                                    className={classes.selectBlock}
                                  >
                                    <MenuItem value=''>None</MenuItem>
                                    {getZoomAttributesByEntity('people').map((option: any, optIndex: number) => {
                                      if (option.sourseKey === '') return null; // Skip the None option from getZoomAttributesByEntity
                                      return (
                                        <MenuItem
                                          key={optIndex}
                                          value={option.sourseKey}
                                          disabled={
                                            isZoomAttributeFixed(option.sourseKey) ||
                                            isZoomAttributeDisabled(option.sourseKey, fieldConfig.destKey || '')
                                          }
                                        >
                                          <WebAssetIcon className={classes.selectIcon} />
                                          {option.sourseLabel}
                                          {isZoomAttributeFixed(option.sourseKey) && ' (Fixed)'}
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                      .filter(Boolean)}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justify='space-between' style={{ marginTop: 20 }}>
                {!isFirstTab && (
                  <Button variant='outlined' onClick={handleBack} className={classes.submitBtn}>
                    Back
                  </Button>
                )}
                <div style={{ marginLeft: 'auto' }}>
                  {isLastTab ? (
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={saveInfoAttributeMappings}
                      className={classes.submitBtn}
                    >
                      Save Mappings
                    </Button>
                  ) : (
                    <Button variant='contained' color='primary' onClick={handleNext} className={classes.submitBtn}>
                      Next
                    </Button>
                  )}
                </div>
              </Grid>
            </TabPanel>
            <TabPanel value={value} index='two'>
              <TableContainer>
                <Table className={`${classes.border} ${classes.boxes}`} aria-label='group attribute mapping table'>
                  <TableHead>
                    <TableRow>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>LDAP Attributes</TableCell>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>Zoom Attributes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getAttributesByEntity('group')
                      .map((attr: any, index: number) => {
                        const fieldConfig = getFieldConfig(attr);
                        // Show row if it has either destKey or sourseKey (for display purposes)
                        if (!fieldConfig.destKey && !fieldConfig.sourseKey && !fieldConfig.sourceKey) return null;

                        return (
                          <TableRow key={index}>
                            <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                              {fieldConfig.destLabel || 'Unknown'}
                              {attr.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                              {fieldConfig.isFromPreference && <span style={{ color: 'blue', marginLeft: 4 }}></span>}
                            </TableCell>
                            <TableCell className={`${classes.border} ${classes.cellLink}`}>
                              {isZoomAttributeFixed(fieldConfig.sourseKey || fieldConfig.sourceKey || '') ? (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              ) : isAttributeEditable(fieldConfig.destKey || '') ? (
                                <FormControl fullWidth>
                                  <Select
                                    key={`${fieldConfig.destKey}-${
                                      getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || 'empty'
                                    }-${mappingsInitialized}`}
                                    value={(() => {
                                      const selectedValue =
                                        getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || '';
                                      // const availableOptions = getZoomAttributesByEntity('group');
                                      // const optionExists = availableOptions.some(opt => opt.sourseKey === selectedValue);
                                      return selectedValue;
                                    })()}
                                    onChange={(e) =>
                                      handleInfoAttributeMapping(fieldConfig.destKey || '', e.target.value as string)
                                    }
                                    displayEmpty
                                    className={classes.selectBlock}
                                  >
                                    <MenuItem value=''>None</MenuItem>
                                    {getZoomAttributesByEntity('group').map((option: any, optIndex: number) => {
                                      if (option.sourseKey === '') return null; // Skip the None option from getZoomAttributesByEntity
                                      return (
                                        <MenuItem
                                          key={optIndex}
                                          value={option.sourseKey}
                                          disabled={
                                            isZoomAttributeFixed(option.sourseKey) ||
                                            isZoomAttributeDisabled(option.sourseKey, fieldConfig.destKey || '')
                                          }
                                        >
                                          <WebAssetIcon className={classes.selectIcon} />
                                          {option.sourseLabel}
                                          {isZoomAttributeFixed(option.sourseKey) && ' (Fixed)'}
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                  {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                      .filter(Boolean)}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justify='space-between' style={{ marginTop: 20 }}>
                {!isFirstTab && (
                  <Button variant='outlined' onClick={handleBack} className={classes.submitBtn}>
                    Back
                  </Button>
                )}
                <div style={{ marginLeft: 'auto' }}>
                  {isLastTab ? (
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={saveInfoAttributeMappings}
                      className={classes.submitBtn}
                    >
                      Save Mappings
                    </Button>
                  ) : (
                    <Button variant='contained' color='primary' onClick={handleNext} className={classes.submitBtn}>
                      Next
                    </Button>
                  )}
                </div>
              </Grid>
            </TabPanel>
            {/* <TabPanel value={value} index='three'>
              <TableContainer>
                <Table className={`${classes.border} ${classes.boxes}`} aria-label='department attribute mapping table'>
                  <TableHead>
                    <TableRow>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>LDAP Attributes</TableCell>
                      <TableCell className={`${classes.border} ${classes.cellTitle}`}>Zoom Attributes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getAttributesByEntity('department').map((attr: any, index: number) => {
                      const fieldConfig = getFieldConfig(attr);
                      // Show row if it has either destKey or sourseKey (for display purposes)
                      if (!fieldConfig.destKey && !fieldConfig.sourseKey && !fieldConfig.sourceKey) return null;

                      return (
                        <TableRow key={index}>
                          <TableCell className={`${classes.border} ${classes.cellTitle}`}>
                            {fieldConfig.destLabel || 'Unknown'}
                            {attr.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                            {fieldConfig.isFromPreference && <span style={{ color: 'blue', marginLeft: 4 }}></span>}
                          </TableCell>
                          <TableCell className={`${classes.border} ${classes.cellLink}`}>
                            {isZoomAttributeFixed(fieldConfig.sourseKey || fieldConfig.sourceKey || '') ? (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>
                                {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                              </span>
                            ) : isAttributeEditable(fieldConfig.destKey || '') ? (
                              <FormControl fullWidth>
                                <Select
                                  key={`${fieldConfig.destKey}-${getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || 'empty'}-${mappingsInitialized}`}
                                  value={(() => {
                                    const selectedValue = getCurrentInfoTabMappings()[fieldConfig.destKey || ''] || '';
                                    const availableOptions = getZoomAttributesByEntity('department');
                                    const optionExists = availableOptions.some(opt => opt.sourseKey === selectedValue);
                                    return selectedValue;
                                  })()}
                                  onChange={(e) => handleInfoAttributeMapping(fieldConfig.destKey || '', e.target.value as string)}
                                  displayEmpty
                                  className={classes.selectBlock}
                                >
                                  <MenuItem value="">None</MenuItem>
                                  {getZoomAttributesByEntity('department').map((option: any, optIndex: number) => {
                                    if (option.sourseKey === '') return null; // Skip the None option from getZoomAttributesByEntity
                                    return (
                                      <MenuItem
                                        key={optIndex}
                                        value={option.sourseKey}
                                        disabled={isZoomAttributeFixed(option.sourseKey) || isZoomAttributeDisabled(option.sourseKey, fieldConfig.destKey || '')}
                                      >
                                        <WebAssetIcon className={classes.selectIcon} />
                                        {option.sourseLabel}
                                        {isZoomAttributeFixed(option.sourseKey) && ' (Fixed)'}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </FormControl>
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>
                                {fieldConfig.sourseLabel || fieldConfig.sourceLabel || 'Unknown'} (Fixed)
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    }).filter(Boolean)}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid container justify="space-between" style={{ marginTop: 20 }}>
                {!isFirstTab && (
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    className={classes.submitBtn}
                  >
                    Back
                  </Button>
                )}
                <div style={{ marginLeft: 'auto' }}>
                  {isLastTab ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={saveInfoAttributeMappings}
                      className={classes.submitBtn}
                    >
                      Save Mappings
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                      className={classes.submitBtn}
                    >
                      Next
                    </Button>
                  )}
                </div>
              </Grid>
            </TabPanel> */}
          </div>
        </Paper>
      </Grid>
    </>
  );
});
